import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { money, moneyShort, dateTime } from '../lib/format';
import { PlaceholderTag } from '../components/lumina/Placeholder';

const TYPE_LABEL: Record<string, string> = {
  sale: 'Venda',
  commission: 'Comissão Storeo',
  reserve_held: 'Reserva retida',
  reserve_released: 'Reserva liberada',
  reversal: 'Estorno',
  chargeback: 'Chargeback',
};

const SIZE = 20;

/** Pagamentos & Repasses — redesign Lumina (docs/prototype/storeo_pagamentos_repasses_pix).
 *  Dado real: balance, ledger, chargebackRate. Cronograma de repasse / auto-repasse /
 *  antecipação / gráfico de fluxo de caixa = placeholder — sem API. */
export function Dinheiro() {
  const [page, setPage] = useState(0);
  const balance = useQuery({ queryKey: ['balance'], queryFn: () => api.balance() });
  const chargeback = useQuery({ queryKey: ['chargebackRate'], queryFn: () => api.chargebackRate() });
  const ledger = useQuery({ queryKey: ['ledger', page], queryFn: () => api.ledger({ page, size: SIZE }) });

  return (
    <div className="lumina flex flex-col gap-space-lg text-on-surface">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-space-md">
        <div className="flex flex-col gap-space-3xs">
          <div className="flex items-center gap-space-xs">
            <span className="font-label-sm text-label-sm uppercase tracking-wider text-primary font-bold">Gestão &amp; Escala</span>
            <span className="text-outline text-label-sm">•</span>
            <span className="font-label-sm text-label-sm text-secondary flex items-center gap-space-3xs">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary" /> Liquidação instantânea D+0
            </span>
          </div>
          <h1 className="font-headline-lg text-headline-lg text-on-surface tracking-tight">Repasses &amp; Tesouraria PIX</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">Seu saldo e o extrato de cada venda, comissão e ajuste.</p>
        </div>
        <span className="px-space-md py-space-xs rounded-full bg-secondary-container/20 text-secondary font-label-sm text-label-sm font-semibold flex items-center gap-space-xs self-start">
          <MSym name="verified_user" className="text-[16px]" /> Gateway SPI/Bacen Ativo
        </span>
      </div>

      {/* 4 cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-space-md">
        <div className="rounded-xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
          <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">Disponível para Saque</span>
          <span className="font-metric-stat text-metric-stat text-secondary font-extrabold tracking-tight">
            {balance.data ? `R$ ${moneyShort(balance.data.available)}` : '—'}
          </span>
          <span className="font-body-sm text-body-sm text-on-surface-variant">Processamento D+0, sem carência bancária</span>
          <button className="mt-space-xs px-space-md py-space-xs rounded-xl bg-secondary/15 text-secondary font-label-md text-label-md font-bold flex items-center justify-center gap-space-xs">
            <MSym name="sync_alt" className="text-[18px]" /> Sacar via PIX 24/7
          </button>
        </div>

        <CardPh label="A Liberar (Cartão / CDC)" value="R$ 14.890,00">
          <span className="font-body-sm text-body-sm text-on-surface-variant">Próxima parcela: hoje às 18:00</span>
        </CardPh>

        <CardReal label="Chargeback (30 dias)" value={chargeback.data ? `${chargeback.data.ratePct.toFixed(2)}%` : '—'}>
          <span className="font-body-sm text-body-sm text-on-surface-variant">
            {chargeback.data ? `${chargeback.data.cardChargebacks} de ${chargeback.data.cardPaid} no cartão` : ''}
          </span>
        </CardReal>

        <div className="rounded-xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
          <div className="flex items-center gap-space-xs">
            <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">Economia em Taxas</span>
            <span className="px-space-2xs py-space-3xs rounded-full bg-secondary/15 text-secondary font-label-sm text-label-sm font-bold">0% take-rate</span>
          </div>
          <span className="font-metric-stat text-metric-stat text-on-surface font-extrabold tracking-tight">7,99%</span>
          <span className="font-body-sm text-body-sm text-on-surface-variant">
            Comissão flat por venda, sem mensalidade nem taxa fixa. A menor do mercado.
          </span>
        </div>
      </div>

      {/* cash flow + panels — placeholder */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-space-md">
        <div className="xl:col-span-8 rounded-2xl bg-surface-container-low p-space-lg shadow-md">
          <div className="flex items-center gap-space-xs mb-space-md">
            <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Fluxo de Caixa &amp; Previsão de Repasses</h2>
            <PlaceholderTag />
          </div>
          <svg className="w-full h-56" fill="none" preserveAspectRatio="none" viewBox="0 0 800 220">
            <defs>
              <linearGradient id="cashGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#4edea3" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#4edea3" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[40, 100, 160].map((y) => (
              <line key={y} stroke="#31353f" strokeDasharray="4 4" strokeWidth="1" x1="0" x2="800" y1={y} y2={y} />
            ))}
            <path d="M0,180 Q120,150 200,140 T360,90 T480,110 T620,50 T800,30 L800,220 L0,220 Z" fill="url(#cashGrad)" />
            <path d="M0,180 Q120,150 200,140 T360,90 T480,110 T620,50 T800,30" fill="none" stroke="#4edea3" strokeWidth="3" strokeLinecap="round" />
            <path d="M0,195 Q160,185 320,170 T640,140 T800,120" fill="none" stroke="#4cd7f6" strokeDasharray="5 5" strokeWidth="2" />
          </svg>
          <div className="flex justify-between font-label-sm text-label-sm text-outline pt-space-2xs">
            <span>Seg</span>
            <span>Ter</span>
            <span>Qua</span>
            <span>Qui</span>
            <span>Sex</span>
            <span>Sáb</span>
            <span>Hoje</span>
          </div>
        </div>

        <div className="xl:col-span-4 flex flex-col gap-space-md">
          <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md">
            <div className="flex items-center justify-between mb-space-sm">
              <div className="flex items-center gap-space-xs">
                <MSym name="autorenew" className="text-secondary text-[20px]" />
                <h3 className="font-headline-sm text-headline-sm text-on-surface font-bold">Auto-Repasse PIX</h3>
              </div>
              <PlaceholderTag />
            </div>
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              Transfere automaticamente para a sua conta sempre que o saldo atingir o gatilho configurado.
            </p>
          </div>
          <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md">
            <div className="flex items-center gap-space-xs mb-space-sm">
              <MSym name="shield" className="text-primary text-[20px]" />
              <h3 className="font-headline-sm text-headline-sm text-on-surface font-bold">Liquidação Direta no SPI</h3>
            </div>
            <p className="font-body-sm text-body-sm text-on-surface-variant">
              O pagamento é processado por instituição certificada. Os dados de cartão nunca passam pelo servidor da Storeo.
            </p>
          </div>
        </div>
      </div>

      {/* extract */}
      <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-md">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-space-xs">
          <div>
            <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Extrato Financeiro</h2>
            <p className="font-body-sm text-body-sm text-on-surface-variant">Registro de cada recebimento, comissão e ajuste.</p>
          </div>
        </div>

        <div className="rounded-2xl bg-surface-container overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse font-body-sm text-body-sm">
              <thead>
                <tr className="bg-surface-container-highest/40 text-on-surface font-label-sm text-label-sm uppercase tracking-wider font-semibold">
                  <th className="py-space-md px-space-lg">ID</th>
                  <th className="py-space-md px-space-md">Tipo</th>
                  <th className="py-space-md px-space-md">Data</th>
                  <th className="py-space-md px-space-md text-right">Valor</th>
                  <th className="py-space-md px-space-lg text-right">Saldo após</th>
                </tr>
              </thead>
              <tbody className="text-on-surface">
                {!ledger.data?.items.length ? (
                  <tr>
                    <td colSpan={5} className="py-space-2xl text-center text-on-surface-variant">
                      {page > 0 ? 'Não há mais lançamentos.' : 'Nada ainda.'}
                    </td>
                  </tr>
                ) : (
                  ledger.data.items.map((l) => (
                    <tr key={l.id} className="hover:bg-surface-container-high/60 transition-colors border-t border-surface-container-highest/30">
                      <td className="py-space-md px-space-lg font-label-sm text-label-sm text-outline">{l.id.slice(0, 8)}</td>
                      <td className="py-space-md px-space-md text-on-surface-variant">{TYPE_LABEL[l.type] ?? l.type}</td>
                      <td className="py-space-md px-space-md text-outline">{dateTime(l.createdAt)}</td>
                      <td className={`py-space-md px-space-md text-right font-label-md text-label-md font-bold whitespace-nowrap ${l.amount < 0 ? 'text-error' : 'text-secondary'}`}>
                        {l.amount < 0 ? '−' : '+'} {money(Math.abs(l.amount))}
                      </td>
                      <td className="py-space-md px-space-lg text-right font-label-md text-label-md font-extrabold text-on-surface whitespace-nowrap">
                        {money(l.balanceAfter)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <button
            className="px-space-md py-space-xs rounded-xl bg-surface-container hover:bg-surface-container-high text-on-surface font-label-md text-label-md transition-all disabled:opacity-40"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ← Mais recentes
          </button>
          <span className="font-label-sm text-label-sm text-outline">Página {page + 1}</span>
          <button
            className="px-space-md py-space-xs rounded-xl bg-surface-container hover:bg-surface-container-high text-on-surface font-label-md text-label-md transition-all disabled:opacity-40"
            disabled={!ledger.data?.hasMore}
            onClick={() => setPage((p) => p + 1)}
          >
            Mais antigos →
          </button>
        </div>
      </div>
    </div>
  );
}

function CardReal({ label, value, children }: { label: string; value: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
      <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">{label}</span>
      <span className="font-metric-stat text-metric-stat text-on-surface font-extrabold tracking-tight">{value}</span>
      {children}
    </div>
  );
}

function CardPh({ label, value, children }: { label: string; value: string; children?: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
      <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider flex items-center">
        {label}
        <PlaceholderTag />
      </span>
      <span className="font-metric-stat text-metric-stat text-on-surface font-extrabold tracking-tight">{value}</span>
      {children}
    </div>
  );
}
